import styled, { css } from 'styled-components'

export interface SectionActionsStyledProps {
  $direction?: 'horizontal' | 'vertical'
  $align?: 'start' | 'center' | 'end' | 'stretch'
  $spacing?: 'small' | 'medium' | 'large'
  $wrap?: boolean
}

const spacingValues = {
  small: '8px',
  medium: '12px',
  large: '16px',
}

const alignmentStyles = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
}

export const Container = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<SectionActionsStyledProps>`
  display: flex;
  align-items: ${({ $align = 'center' }) => alignmentStyles[$align]};

  ${({ $direction = 'horizontal', $align = 'center' }) =>
    $direction === 'horizontal'
      ? css`
          flex-direction: row;
          justify-content: ${alignmentStyles[$align]};
        `
      : css`
          flex-direction: column;
          align-items: ${alignmentStyles[$align]};
        `}

  ${({ $wrap = false, $direction = 'horizontal' }) =>
    $wrap && $direction === 'horizontal'
      ? css`
          flex-wrap: wrap;
        `
      : ''}
  
  gap: ${({ $spacing = 'medium' }) => spacingValues[$spacing]};

  ${({ $align = 'center' }) =>
    $align === 'stretch'
      ? css`
          > * {
            flex: 1;
          }
        `
      : ''}
`

export const ActionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{
  $variant?: 'primary' | 'secondary' | 'ghost'
  $size?: 'small' | 'medium' | 'large'
  $loading?: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  ${({ $loading }) =>
    $loading &&
    css`
      cursor: wait;
      opacity: 0.7;
    `}

  ${({ $size = 'medium' }) => {
    switch ($size) {
      case 'small':
        return css`
          padding: 6px 12px;
          font-size: ${({ theme }) => theme.typography.fontSize.small};
          line-height: ${({ theme }) => theme.typography.lineHeight.tight};
        `
      case 'large':
        return css`
          padding: 12px 20px;
          font-size: ${({ theme }) => theme.typography.fontSize.body};
          line-height: ${({ theme }) => theme.typography.lineHeight.normal};
        `
      default:
        return css`
          padding: 8px 16px;
          font-size: ${({ theme }) => theme.typography.fontSize.body};
          line-height: ${({ theme }) => theme.typography.lineHeight.normal};
        `
    }
  }}
  
  ${({ $variant = 'secondary', theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: ${theme.colors.text.inverse};

          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
          }

          &:active:not(:disabled) {
            background: ${theme.colors.primaryHover};
          }
        `
      case 'ghost':
        return css`
          background: transparent;
          color: ${theme.colors.text.secondary};

          &:hover:not(:disabled) {
            background: ${theme.colors.background.secondary};
            color: ${theme.colors.text.primary};
          }

          &:active:not(:disabled) {
            background: ${theme.colors.background.tertiary};
          }
        `
      default:
        return css`
          background: ${theme.colors.background.secondary};
          color: ${theme.colors.text.primary};
          border: 1px solid ${theme.colors.border.light};

          &:hover:not(:disabled) {
            background: ${theme.colors.background.tertiary};
            border-color: ${theme.colors.border.default};
          }

          &:active:not(:disabled) {
            background: ${theme.colors.background.tertiary};
            border-color: ${theme.colors.border.default};
          }
        `
    }
  }}
`

export const OverflowMenu = styled.div`
  position: relative;
  display: inline-flex;
`

export const OverflowButton = styled(ActionButton)`
  min-width: 40px;
`

export const OverflowContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !prop.startsWith('$'),
})<{ $open: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 160px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.light};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 4px 0;
  margin-top: 4px;

  ${({ $open }) =>
    $open
      ? css`
          display: block;
        `
      : css`
          display: none;
        `}
`

export const OverflowItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`
